import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import { makeStyles } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import PropTypes from 'prop-types'
import React from 'react'

/**
 * Draw-with-your-finger signature capture.
 *
 * No library: signature-pad is a few dozen lines of pointer events, and adding a
 * dependency to a repo whose deploy mounts code over a PRE-BUILT image means the
 * package never actually ships (see the pillow-heif lesson in the ID-card work).
 *
 * Pointer events, not mouse events, so it works with a finger on a phone — the
 * parent signing the underage undertaking is almost certainly on one.
 *
 * The value is a PNG data URL. Empty string means "not signed", and the parent
 * form treats that as a hard stop.
 */
const useStyles = makeStyles((theme) => ({
  wrap: { width: '100%' },
  canvas: {
    width: '100%',
    height: 160,
    border: '1px dashed #bdbdbd',
    borderRadius: 6,
    background: '#fff',
    // Without this a finger-drag scrolls the page instead of drawing.
    touchAction: 'none',
    cursor: 'crosshair',
    display: 'block'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5)
  }
}))

const SignaturePad = ({ label, value, onChange, disabled }) => {
  const classes = useStyles()
  const canvasRef = React.useRef(null)
  const drawing = React.useRef(false)
  const [dirty, setDirty] = React.useState(Boolean(value))

  // Size the bitmap to the element's real pixel size, or the stroke lands
  // somewhere other than the finger.
  const setup = React.useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    canvas.width = rect.width * ratio
    canvas.height = rect.height * ratio
    const ctx = canvas.getContext('2d')
    ctx.scale(ratio, ratio)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#1a1a1a'
    if (value) {
      const img = new Image()
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height)
      img.src = value
    }
  }, [value])

  React.useEffect(() => {
    setup()
    window.addEventListener('resize', setup)
    return () => window.removeEventListener('resize', setup)
  }, [setup])

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const start = (e) => {
    if (disabled) return
    drawing.current = true
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = pos(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    canvasRef.current.setPointerCapture(e.pointerId)
  }

  const move = (e) => {
    if (!drawing.current || disabled) return
    const ctx = canvasRef.current.getContext('2d')
    const { x, y } = pos(e)
    ctx.lineTo(x, y)
    ctx.stroke()
    setDirty(true)
  }

  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    onChange(canvasRef.current.toDataURL('image/png'))
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setDirty(false)
    onChange('')
  }

  return (
    <Box className={classes.wrap}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <canvas
        ref={canvasRef}
        className={classes.canvas}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <Box className={classes.row}>
        <Typography variant="caption" color="textSecondary">
          {dirty ? 'signed' : 'sign above using your finger or mouse'}
        </Typography>
        {!disabled && dirty ? (
          <Button size="small" onClick={clear}>
            clear
          </Button>
        ) : null}
      </Box>
    </Box>
  )
}

SignaturePad.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

SignaturePad.defaultProps = {
  label: 'signature',
  value: '',
  disabled: false
}

export default SignaturePad
